/**
 * Addresses and areas API. Used by AddressListScreen, AddressFormScreen, CheckoutScreen.
 */
import { apiGet, apiPost, apiPostFormData, apiPut, apiDelete } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';
import type { Address, AddressListResponse, Area, AreasResponse } from './apiTypes';

export type { Address, Area };

export interface AddressPayload {
  customer_id: number;
  name: string;
  area_id: number;
  block: string;
  street: string;
  house_name: string;
  floor?: string;
  flat?: string;
  landmark?: string;
  phone: string;
  is_default?: boolean;
  slot_id?: number;
  governorate_id?: number;
  driver_note?: string;
  PACI_number?: string;
  zip_code?: string;
  avenue?: string;
}

function listPath(): string {
  return endpoints.addresses;
}

function singlePath(id: number | string): string {
  return `${endpoints.address}/${id}`;
}

/** Coerce API `is_default` (`1`/`0`/`"1"`/`"0"`/bool) — string `"0"` must not count as default. */
export function coerceAddressIsDefault(value: unknown): boolean {
  if (value === true || value === 1) {
    return true;
  }
  if (value === false || value === 0 || value == null) {
    return false;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes';
  }
  return false;
}

function normalizeAddress(raw: any): Address | null {
  const id = Number(raw?.id ?? 0);
  if (!Number.isFinite(id) || id <= 0) {
    return null;
  }
  const customer_id = Number(raw?.customer_id ?? 0);
  const area_id = Number(raw?.area_id ?? 0);
  return {
    id,
    customer_id: Number.isFinite(customer_id) ? customer_id : 0,
    name: String(raw?.name ?? ''),
    area_id: Number.isFinite(area_id) ? area_id : 0,
    governorate_id:
      raw?.governorate_id != null && raw.governorate_id !== ''
        ? Number(raw.governorate_id)
        : undefined,
    area_name: String(raw?.area_name ?? raw?.area?.name ?? ''),
    block: String(raw?.block ?? ''),
    street: String(raw?.street ?? ''),
    house_name: String(raw?.house_name ?? ''),
    floor: raw?.floor != null ? String(raw.floor) : undefined,
    flat: raw?.flat != null ? String(raw.flat) : undefined,
    landmark: raw?.landmark != null ? String(raw.landmark) : undefined,
    phone: String(raw?.phone ?? ''),
    is_default: coerceAddressIsDefault(raw?.is_default),
  };
}

/** At most one default — if API returns several, keep the highest id. */
function enforceSingleDefault(addresses: Address[]): Address[] {
  const defaults = addresses.filter(a => a.is_default);
  if (defaults.length <= 1) {
    return addresses;
  }
  const keep_id = Math.max(...defaults.map(a => a.id));
  return addresses.map(a => (a.is_default && a.id !== keep_id ? { ...a, is_default: false } : a));
}

function extractAddressList(res: unknown): any[] {
  if (Array.isArray((res as AddressListResponse)?.data)) {
    return (res as AddressListResponse).data;
  }
  const data = (res as any)?.data;
  if (data?.addresses && Array.isArray(data.addresses)) {
    return data.addresses;
  }
  if (Array.isArray((res as any)?.addresses)) {
    return (res as any).addresses;
  }
  if (Array.isArray(res)) {
    return res;
  }
  return [];
}

export async function fetchAddresses(customer_id: number): Promise<Address[]> {
  const res = await apiGet<AddressListResponse | { data?: { addresses?: Address[] }; addresses?: Address[] }>(listPath(), { customer_id });
  const normalized = extractAddressList(res)
    .map(normalizeAddress)
    .filter((a): a is Address => a != null);
  return enforceSingleDefault(normalized);
}

function governorate_id_from_raw(raw: any): number | undefined {
  const direct = raw?.governorate_id ?? raw?.governorateId;
  if (direct != null && direct !== '') {
    const n = Number(direct);
    if (!Number.isNaN(n) && n > 0) {
      return n;
    }
  }
  const gov = raw?.governorate;
  if (gov != null && typeof gov === 'object' && (gov as { id?: unknown }).id != null) {
    const n = Number((gov as { id: unknown }).id);
    if (!Number.isNaN(n) && n > 0) {
      return n;
    }
  }
  return undefined;
}

/** Normalize API area item to Area shape (backend may send title/a_title like the previous app). */
function normalizeArea(raw: any): Area {
  const id = Number(raw?.id ?? raw?.area_id ?? 0);
  const name = raw?.name ?? raw?.title ?? '';
  const name_ar = raw?.name_ar ?? raw?.a_title ?? raw?.title_ar ?? '';
  const governorate_id = governorate_id_from_raw(raw);
  return {
    id,
    name: String(name),
    name_ar: String(name_ar),
    ...(governorate_id != null ? { governorate_id } : {}),
    governorate: typeof raw?.governorate === 'string' ? raw.governorate : (raw?.governorate as { name?: string } | undefined)?.name,
    delivery_available: raw?.delivery_available ?? true,
    order_delivery_charge: raw?.order_delivery_charge != null ? Number(raw.order_delivery_charge) : undefined,
    delivery_charge: raw?.delivery_charge != null ? Number(raw.delivery_charge) : undefined,
  };
}

function extractAreaList(res: any): any[] {
  if (res == null) {return [];}
  if (Array.isArray(res)) {return res;}
  const r = res as Record<string, unknown>;
  if (Array.isArray(r?.data)) {return r.data as any[];}
  if (Array.isArray(r?.areas)) {return r.areas as any[];}
  if (r?.data && typeof r.data === 'object' && !Array.isArray(r.data)) {
    const data = r.data as Record<string, unknown>;
    if (Array.isArray(data.areas)) {return data.areas as any[];}
    if (Array.isArray(data.data)) {return data.data as any[];}
    if (Array.isArray(data.list)) {return data.list as any[];}
    if (Array.isArray(data.items)) {return data.items as any[];}
    if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
      const vals = Object.values(data);
      if (vals.length > 0 && vals.every(v => typeof v === 'object' && v !== null && (v as any).id != null)) {return vals as any[];}
    }
  }
  if (Array.isArray(r?.list)) {return r.list as any[];}
  if (Array.isArray(r?.items)) {return r.items as any[];}
  if (typeof r === 'object' && r !== null && !Array.isArray(r)) {
    const vals = Object.values(r);
    if (vals.length > 0 && vals.every(v => typeof v === 'object' && v !== null)) {return vals as any[];}
  }
  return [];
}

export interface DeliverySlot {
  id: number;
  start_time?: string;
  end_time?: string;
  is_active?: boolean;
  [key: string]: unknown;
}

export async function fetchDeliverySlots(area_id: number | string): Promise<DeliverySlot[]> {
  try {
    const res = await apiGet<{ success?: boolean; data?: { slots?: DeliverySlot[] }; slots?: DeliverySlot[] }>(
      endpoints.slots,
      { area_id },
    );
    const slots = (res as any)?.data?.slots ?? (res as any)?.slots ?? [];
    if (!Array.isArray(slots)) {
      return [];
    }
    return slots.filter(slot => slot.is_active !== false);
  } catch {
    return [];
  }
}

export async function fetchAreas(): Promise<Area[]> {
  try {
    const res = await apiGet<AreasResponse | { data?: Area[] | Record<string, unknown>; areas?: Area[] } | Area[]>(endpoints.areas);
    const list = extractAreaList(res);
    return list.map(normalizeArea).filter(a => a.id > 0 || a.name !== '');
  } catch (_) {
    return [];
  }
}

export async function createAddress(payload: AddressPayload): Promise<Address> {
  const url = payload.customer_id != null ? `${endpoints.address}?customer_id=${payload.customer_id}` : endpoints.address;
  const res = await apiPost<{ data?: Address; success?: boolean }>(url, payload as object);
  const created = normalizeAddress((res as any).data ?? res) ?? ((res as any).data ?? res);
  if (payload.is_default && created?.id != null && payload.customer_id != null) {
    await setDefaultAddress(created.id, payload.customer_id);
  }
  return created as Address;
}

export async function updateAddress(address_id: number, customer_id: number, payload: Partial<AddressPayload>): Promise<Address> {
  const url = `${singlePath(address_id)}?customer_id=${customer_id}`;
  const res = await apiPut<{ data?: Address }>(url, payload as object);
  const updated = normalizeAddress((res as any).data ?? res) ?? ((res as any).data ?? res);
  if (payload.is_default) {
    await setDefaultAddress(address_id, customer_id);
  }
  return updated as Address;
}

export async function deleteAddress(address_id: number, customer_id?: number): Promise<void> {
  const url = customer_id != null ? `${singlePath(address_id)}?customer_id=${customer_id}` : singlePath(address_id);
  await apiDelete(url);
}

export async function setDefaultAddress(address_id: number, customer_id?: number): Promise<Address | void> {
  if (customer_id == null) {return;}
  const url = `${endpoints.setDefault}?customer_id=${customer_id}&address_id=${address_id}`;
  await apiGet(url);
}

export interface DeliveryAddressRequest {
  customer_id: number;
  customer_subscription_id: number;
  dates: string[];
  address_id: number;
}

/**
 * POST delivery-address assignment. Backend expects multipart form fields
 * (`dates[]` repeated) — see Postman “Set Address For subscription”.
 */
export async function setDeliveryAddressDates(requests: DeliveryAddressRequest[]): Promise<void> {
  await Promise.all(
    requests.map(req => {
      const form = new FormData();
      form.append('customer_id', String(req.customer_id));
      form.append('customer_subscription_id', String(req.customer_subscription_id));
      form.append('address_id', String(req.address_id));
      for (const date of req.dates) {
        form.append('dates[]', date);
      }
      return apiPostFormData(endpoints.deliverAddressDates, form);
    }),
  );
}
